/**
 * Environment configuration for different deployment stages
 * This provides a centralized way to handle environment-specific settings
 * following Amazon's standard patterns for local development
 */

export type Environment = 'local' | 'development' | 'alpha' | 'beta' | 'gamma' | 'production';

export interface EnvironmentConfig {
  environment: Environment;
  apiUrl: string;
  enableAuth: boolean;
  enableMockData: boolean;
  enableDebugMode: boolean;
  enableAnalytics: boolean;
  settingsUrl?: string;
}

/**
 * Get environment configuration based on current environment
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  // Default to local for development
  const env = (process.env.NODE_ENV === 'production' ? 'production' : 'local') as Environment;
  
  const baseConfig: Record<Environment, EnvironmentConfig> = {
    local: {
      environment: 'local',
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
      enableAuth: false, // Disabled for local development
      enableMockData: true,
      enableDebugMode: true,
      enableAnalytics: false,
    },
    development: {
      environment: 'development',
      apiUrl: process.env.REACT_APP_API_URL || '',
      enableAuth: true,
      enableMockData: true,
      enableDebugMode: true,
      enableAnalytics: false,
    },
    alpha: {
      environment: 'alpha',
      apiUrl: process.env.REACT_APP_API_URL || '',
      enableAuth: true,
      enableMockData: true,
      enableDebugMode: true,
      enableAnalytics: false,
    },
    beta: {
      environment: 'beta',
      apiUrl: process.env.REACT_APP_API_URL || '',
      enableAuth: true,
      enableMockData: false,
      enableDebugMode: true,
      enableAnalytics: true,
    },
    gamma: {
      environment: 'gamma',
      apiUrl: process.env.REACT_APP_API_URL || '',
      enableAuth: true,
      enableMockData: false,
      enableDebugMode: false,
      enableAnalytics: true,
    },
    production: {
      environment: 'production',
      apiUrl: process.env.REACT_APP_API_URL || '',
      enableAuth: true,
      enableMockData: false,
      enableDebugMode: false,
      enableAnalytics: true,
    },
  };

  return baseConfig[env];
}

/**
 * Check if running in local development mode
 */
export function isLocalDevelopment(): boolean {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return hostname === 'localhost' || hostname.includes('localhost.a2z.com') || process.env.NODE_ENV === 'development';
}

/**
 * Check if running in any development/testing mode
 */
export function isDevelopmentMode(): boolean {
  const env = getEnvironmentConfig().environment;
  return ['local', 'development', 'alpha'].includes(env);
}

/**
 * Detect the current deployment environment based on hostname and settings
 */
export function detectDeploymentEnvironment(): string {
  if (typeof window === 'undefined') return 'local';
  
  const hostname = window.location.hostname;
  
  // Local development patterns
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname.includes('localhost.a2z.com')) {
    return 'local';
  }
  
  // For CloudFront deployments (dev stacks), use the stage from settings.json if available
  if (hostname.includes('cloudfront.net')) {
    // Check if settings are already loaded globally
    const globalSettings = (window as any).__ADDAPT_SETTINGS__;
    if (globalSettings?.stage) {
      return globalSettings.stage;
    }
    // Return placeholder if settings not yet loaded - useSettings will handle environment correctly
    return 'dev-stack';
  }
  
  // Amazon internal domains (pipeline deployments with custom domains)
  if (hostname.includes('alpha.addapt.fintech.amazon.dev')) {
    return 'alpha';
  }
  if (hostname.includes('beta.addapt.fintech.amazon.dev')) {
    return 'beta';
  }
  if (hostname.includes('gamma.addapt.fintech.amazon.dev')) {
    return 'gamma';
  }
  if (hostname.includes('addapt.fintech.amazon.dev')) {
    return 'production';
  }
  
  // Default fallback
  return 'production';
}

/**
 * Get the deployment environment from settings (preferred for CloudFront deployments)
 * This should be used after settings are loaded
 */
export function getEnvironmentFromSettings(): string | null {
  if (typeof window === 'undefined') return null;
  
  const globalSettings = (window as any).__ADDAPT_SETTINGS__;
  return globalSettings?.stage || null;
}

/**
 * Get the appropriate settings URL based on the current environment
 * This follows Amazon's standard pattern for dynamic configuration loading
 * 
 * Amazon Standard Pattern:
 * - Local development (localhost.a2z.com): /local-settings.json
 * - Dev stack (CloudFront): /settings.json (generated by CDK)
 * - Pipeline deployments (custom domains): /settings.json (generated by CDK)
 */
export function getSettingsUrl(): string {
  const environment = detectDeploymentEnvironment();
  
  // For local development (brazil-build server), use local-settings.json
  if (environment === 'local') {
    return '/local-settings.json';
  }
  
  // For all deployed environments (dev stack and pipeline), use settings.json (generated by CDK)
  return '/settings.json';
}

/**
 * Check if we're running in a CloudFront deployment (dev stack)
 */
export function isCloudFrontDeployment(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.includes('cloudfront.net');
}

/**
 * Get the current CloudFront domain (for dev stack deployments)
 */
export function getCloudFrontDomain(): string | null {
  if (!isCloudFrontDeployment()) return null;
  return `https://${window.location.hostname}`;
}
